import type { RouteObject } from "react-router-dom";
import {
  getModuleBasePath,
  type ModuleInstance,
  type ModuleLayout,
  type ModuleRouteAuth,
  type ModuleRouteHandle,
} from "@/modules";
import {
  GuestOutlet,
  OptionalAuthOutlet,
  PrivateOutlet,
  ShellLayout,
} from "./route-guards";

/**
 * Route ağacını kurar.
 *
 * Girdi: `handle: { auth, layout, moduleId? }` etiketli düz route listesi.
 * Çıktı: layout → auth katmanlarına gruplanmış ağaç. Data router ranking'i
 * path'siz layout route'larından etkilenmez; `*` catch-all en sona düşer.
 *
 *   AppRoot
 *   ├─ ShellLayout
 *   │   ├─ PrivateOutlet      → shell + private
 *   │   ├─ OptionalAuthOutlet → shell + optional
 *   │   └─ (public)           → shell + public
 *   ├─ PrivateOutlet          → bare + private
 *   ├─ OptionalAuthOutlet     → bare + optional
 *   ├─ GuestOutlet            → bare + guest (login/register)
 *   └─ (public)               → bare + public (redirect'ler, OAuth callback)
 */

const readHandle = (route: RouteObject): ModuleRouteHandle =>
  typeof route.handle === "object" && route.handle
    ? (route.handle as ModuleRouteHandle)
    : {};

const layoutOf = (route: RouteObject): ModuleLayout =>
  readHandle(route).layout ?? "shell";
const authOf = (route: RouteObject): ModuleRouteAuth =>
  readHandle(route).auth ?? "private";

/**
 * Bir instance'ın blueprint route'larını mutlak path'li düz listeye çevirir.
 * `index` route'u modül köküne (`basePath`) exact path olur; içindeki
 * relative `<Navigate to="shop">` yine `basePath/shop`'a çözülür.
 */
export const buildModuleRoutes = (
  instance: ModuleInstance,
  blueprintRoutes: RouteObject[],
): RouteObject[] => {
  const base = getModuleBasePath(instance); // "" (host) | "/fashion"

  const extra = instance.extensions?.routes ?? [];

  return [...blueprintRoutes, ...extra].map((route) => {
    const { index, path, handle, ...rest } = route as RouteObject & {
      index?: boolean;
      path?: string;
    };
    const seg = (path ?? "").replace(/^\/+/, "");
    const absolute = index || !seg ? base || "/" : `${base}/${seg}`;

    return {
      ...rest,
      path: absolute,
      handle: {
        ...(typeof handle === "object" && handle ? handle : {}),
        moduleId: instance.id,
      } satisfies ModuleRouteHandle,
    } as RouteObject;
  });
};

const groupBy = <K extends string>(
  routes: RouteObject[],
  key: (r: RouteObject) => K,
): Record<K, RouteObject[]> =>
  routes.reduce(
    (acc, r) => {
      const k = key(r);
      (acc[k] ??= []).push(r);
      return acc;
    },
    {} as Record<K, RouteObject[]>,
  );

const wrapAuth = (routes: RouteObject[]): RouteObject[] => {
  const byAuth = groupBy(routes, authOf);
  const out: RouteObject[] = [];

  if (byAuth.private?.length) {
    out.push({ element: <PrivateOutlet />, children: byAuth.private });
  }
  if (byAuth.optional?.length) {
    out.push({ element: <OptionalAuthOutlet />, children: byAuth.optional });
  }
  if (byAuth.guest?.length) {
    out.push({ element: <GuestOutlet />, children: byAuth.guest });
  }
  if (byAuth.public?.length) {
    out.push(...byAuth.public);
  }
  return out;
};

/** Düz listeyi layout → auth ağacına çevirir; AppRoot'un `children`'ı olur. */
export const buildRouteTree = (routes: RouteObject[]): RouteObject[] => {
  const byLayout = groupBy(routes, layoutOf);
  const tree: RouteObject[] = [];

  if (byLayout.shell?.length) {
    tree.push({ element: <ShellLayout />, children: wrapAuth(byLayout.shell) });
  }
  if (byLayout.bare?.length) {
    tree.push(...wrapAuth(byLayout.bare));
  }
  return tree;
};
